import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star, ChevronDown } from "lucide-react";
import { IMAGES, BIZ } from "../data";
import { CtaButton } from "../components/Cta";

const HEADLINE = ["TRANSFORMAÇÃO", "FÍSICA DE", "ALTO NÍVEL"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const lineInner = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};
const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-black pb-16 pt-28"
      data-testid="hero-section"
    >
      {/* Background image + parallax */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Treino de musculação intenso"
          className="h-full w-full object-cover object-center opacity-60"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/30 to-transparent" />
      </motion.div>

      {/* Gold spotlight */}
      <div className="pointer-events-none absolute -right-40 top-10 z-0 h-[500px] w-[500px] rounded-full bg-[--gold] opacity-[0.10] blur-[130px]" />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8"
      >
        {/* Rating badge */}
        <motion.div
          variants={fadeUp}
          className="mb-6 inline-flex items-center gap-3 border border-[--gold]/30 bg-white/5 px-4 py-2 backdrop-blur-md"
          data-testid="hero-rating-badge"
        >
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-[--gold] text-[--gold]" />
            ))}
          </div>
          <span className="font-display text-sm font-bold uppercase tracking-wider text-white">
            {BIZ.rating.score} · {BIZ.rating.count} avaliações no Google
          </span>
        </motion.div>

        <motion.p
          variants={fadeUp}
          className="mb-3 font-display text-xs font-semibold uppercase tracking-[0.35em] text-[--gold] sm:text-sm"
        >
          {BIZ.name} — Jundiaí-SP
        </motion.p>

        {/* Masked line-by-line reveal */}
        <h1 className="font-display font-black uppercase tracking-tight text-white">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden py-[0.04em]">
              <motion.span
                variants={lineInner}
                className={`block text-[2.9rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-8xl ${
                  i === 1 ? "text-[--gold] gold-glow" : ""
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          variants={fadeUp}
          className="mt-6 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
        >
          Treinamento resistido personalizado em studio privativo, com{" "}
          <span className="text-white">{BIZ.yearsExp} anos de experiência</span> e acompanhamento de
          perto. Cada treino é construído para o seu corpo e a sua meta.
        </motion.p>

        <motion.div variants={fadeUp} className="mt-8">
          <CtaButton size="lg" testid="hero-whatsapp-btn" />
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="h-6 w-6 text-[--gold]/70" />
        </motion.div>
      </div>
    </section>
  );
}
